/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiComboBox, EuiFormRow, EuiIcon } from "@inscopix/ideas-eui";
import { uniq } from "lodash";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { TABLE_ICONS } from "types/Icons";
import { isDefined } from "utils/isDefined";

const styles = {
  disabled: css`
    & button {
      display: none;
    }
  `,
};

interface FieldTableComboBoxProps {
  label?: string;
  includeTable: (table: DataTableStoreState["tables"][number]) => boolean;
  disabledTableIds: string[];
  value: string[];
  onChange: (tableIds: string[]) => void;
  disabled?: boolean;
  singleSelect?: boolean;
}

export const FieldTableComboBox = ({
  label = "Tables",
  includeTable,
  disabledTableIds,
  value,
  onChange,
  disabled = false,
  singleSelect = false,
}: FieldTableComboBoxProps) => {
  const tables = useDataTableContext((s) => s.tables.filter(includeTable));

  const options = tables.map((table, idx) => ({
    id: table.id,
    key: table.key,
    label: `${table.name} (${table.key})`,
    prepend: <EuiIcon size="s" type={TABLE_ICONS[table.kind]} />,
    value: {
      id: table.id,
      kind: table.kind,
      key: table.key,
      name: table.name,
    },
    // Hide the remove button if table ID is disabled
    css: disabledTableIds.includes(table.id) ? styles.disabled : undefined,
  }));

  return (
    <EuiFormRow label={label} fullWidth>
      <EuiComboBox<(typeof options)[number]["value"]>
        aria-label="Add tables"
        fullWidth
        rowHeight={40}
        placeholder={"Select source table" + (singleSelect ? "" : "(s)")}
        isClearable={false}
        options={options}
        onKeyDown={(e) => e.stopPropagation()}
        selectedOptions={options.filter((option) => value.includes(option.id))}
        onChange={(options) => {
          // Prevent disabled table IDs from being removed
          const tableIds = options.map((option) => option.id).filter(isDefined);
          onChange(uniq([...disabledTableIds, ...tableIds]));
        }}
        isDisabled={disabled}
        singleSelection={singleSelect}
      />
    </EuiFormRow>
  );
};
